import { useLiveQuery } from 'dexie-react-hooks'
import { db } from '@/lib/db'
import { supabase } from '@/lib/supabase'
import { seasonToRow } from '@/lib/mappers'
import type { Season } from '@/types'
import { v4 as uuid } from 'uuid'

export function useSeasons(teamId: string | undefined) {
  return useLiveQuery(async () => {
    if (!teamId) return []
    return db.seasons.where('teamId').equals(teamId).sortBy('year')
  }, [teamId])
}

export async function addSeason(data: Omit<Season, 'id' | 'createdAt' | 'updatedAt'>): Promise<Season> {
  const now = new Date().toISOString()

  if (data.isActive) {
    // Deactivate all other seasons for this team in Supabase
    await supabase.from('seasons').update({ is_active: false, updated_at: now }).eq('team_id', data.teamId)
    // Mirror in Dexie
    const existing = await db.seasons.where('teamId').equals(data.teamId).toArray()
    for (const s of existing) {
      if (s.isActive) await db.seasons.update(s.id, { isActive: false, updatedAt: now })
    }
  }

  const season: Season = { ...data, id: uuid(), createdAt: now, updatedAt: now }
  const { error } = await supabase.from('seasons').upsert(seasonToRow(season))
  if (error) throw new Error(error.message)
  await db.seasons.put(season)
  return season
}

export async function setActiveSeason(seasonId: string, teamId: string): Promise<void> {
  const now = new Date().toISOString()
  // Deactivate all, then activate the target
  const { error: deactivateErr } = await supabase
    .from('seasons')
    .update({ is_active: false, updated_at: now })
    .eq('team_id', teamId)
  if (deactivateErr) throw new Error(deactivateErr.message)

  const { error: activateErr } = await supabase
    .from('seasons')
    .update({ is_active: true, updated_at: now })
    .eq('id', seasonId)
  if (activateErr) throw new Error(activateErr.message)

  // Mirror in Dexie
  const all = await db.seasons.where('teamId').equals(teamId).toArray()
  await Promise.all(all.map(s => db.seasons.update(s.id, { isActive: s.id === seasonId, updatedAt: now })))
}

export async function toggleSeasonLock(id: string, locked: boolean): Promise<void> {
  const now = new Date().toISOString()
  const { error } = await supabase.from('seasons').update({ is_locked: locked, updated_at: now }).eq('id', id)
  if (error) throw new Error(error.message)
  await db.seasons.update(id, { isLocked: locked, updatedAt: now })
}

export async function deleteSeason(id: string): Promise<void> {
  // Supabase cascade handles: games → lineups → lineup_slots → stat_entries, season_rosters, events
  const { error } = await supabase.from('seasons').delete().eq('id', id)
  if (error) throw new Error(error.message)

  // Mirror cascade in Dexie
  await db.transaction('rw', [db.seasons, db.seasonRosters, db.games, db.lineups, db.lineupSlots, db.statEntries, db.events], async () => {
    const games = await db.games.where('seasonId').equals(id).toArray()
    for (const g of games) {
      const lineups = await db.lineups.where('gameId').equals(g.id).toArray()
      for (const l of lineups) await db.lineupSlots.where('lineupId').equals(l.id).delete()
      await db.lineups.where('gameId').equals(g.id).delete()
      await db.statEntries.where('gameId').equals(g.id).delete()
    }
    await db.games.where('seasonId').equals(id).delete()
    await db.seasonRosters.where('seasonId').equals(id).delete()
    await db.events.where('seasonId').equals(id).delete()
    await db.seasons.delete(id)
  })
}
